import React, {
  type ReactElement,
  useCallback,
  useEffect,
  useState,
} from "react";

import { useTranslation } from "react-i18next";

import { FilmIcon } from "@lucide/icons";

import { handleClickForKeyboard } from "simplydo/core/accessibility";
import { Button } from "simplydo/react/ui/button";
import { H5, Subheader } from "simplydo/react/ui/header";
import { Icon } from "simplydo/react/ui/icon";
import { Input } from "simplydo/react/ui/input";
import { Popup } from "simplydo/react/ui/popup";

type IFrameChooserProps = {
  trigger?: ReactElement;
  isBasic?: boolean;
  isOpen?: boolean;
  onClose: () => void;
  onOpen?: () => void;
  onComplete?: (IFrameURL: string) => void;
};

export const IFrameChooser = ({
  trigger,
  isOpen,
  onClose,
  onOpen,
  onComplete,
  isBasic,
}: IFrameChooserProps) => {
  const [open, setOpen] = useState(false);
  const [IFrameURL, setIFrameURL] = useState("");
  const { t } = useTranslation();

  useEffect(() => {
    if (isOpen !== undefined && open !== isOpen) {
      setOpen(isOpen);
    }
  }, [open, isOpen]);

  const togglePopup = () => {
    if (!open && onOpen) onOpen();
    if (open && onClose) onClose();
    setOpen(!open);
  };

  const insertIFrame = useCallback(() => {
    onComplete?.(IFrameURL);
    setIFrameURL("");
  }, [IFrameURL, onComplete]);

  return (
    <Popup
      on="click"
      style={{ zIndex: 5 }}
      open={open}
      position="bottom left"
      trigger={
        trigger ? (
          React.cloneElement(trigger, {
            onClick: togglePopup,
          } as React.HTMLAttributes<HTMLElement>)
        ) : (
          <Button
            size="small"
            fluid
            onClick={togglePopup}
            color="secondary"
            variant={isBasic ? "outline" : "regular"}
          >
            <Icon icon={FilmIcon} />
            Enter an iFrame URL
          </Button>
        )
      }
      content={
        <div>
          <H5>
            Insert an iframe
            <Subheader size="small">
              Please only insert the URL portion of the iframe. At this time, we
              currently allow the following sources for iframes: YouTube, Vimeo,
              PowerBI
            </Subheader>
          </H5>

          <Input
            size="small"
            fluid
            placeholder="Enter embed code or link to iframe..."
            onChange={(e) => setIFrameURL(e.target.value)}
            onKeyDown={handleClickForKeyboard(insertIFrame, {
              keys: ["Enter"],
            })}
            onClick={(e: React.MouseEvent<HTMLInputElement>) => {
              (e.target as HTMLInputElement).focus();
            }}
            action={
              <Button onClick={insertIFrame} color="primary">
                Insert
              </Button>
            }
          />
          <div style={{ textAlign: "left", marginTop: 20 }}>
            <Button size="small" onClick={togglePopup} variant="outline">
              {t("generic.cancel")}
            </Button>
          </div>
        </div>
      }
    />
  );
};
